import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  ShoppingCart, 
  TestTube, 
  DollarSign, 
  MessageSquare,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Edit,
  Trash2,
  Eye,
  Users
} from 'lucide-react';
import UserManagementModal from '../components/UserManagementModal';
import InternalChat from '../components/InternalChat';
import authService from '../services/authApi';
import { laboratoryService, supplierService } from '../services/providersApi';
import staffService from '../services/staffApi';

const Administration: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inventory');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalMode, setUserModalMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [teamUsers, setTeamUsers] = useState([
    { id: 1, firstName: 'Dr. María', lastName: 'García', email: 'maria.garcia@plannitas.com', role: 'admin', status: 'active', lastLogin: '2024-10-25 09:30' },
    { id: 2, firstName: 'Dr. Carlos', lastName: 'Rodríguez', email: 'carlos.rodriguez@plannitas.com', role: 'dentist', status: 'active', lastLogin: '2024-10-25 08:45' },
    { id: 3, firstName: 'Ana', lastName: 'López', email: 'ana.lopez@plannitas.com', role: 'receptionist', status: 'active', lastLogin: '2024-10-25 10:15' },
    { id: 4, firstName: 'Luis', lastName: 'Martínez', email: 'luis.martinez@plannitas.com', role: 'assistant', status: 'inactive', lastLogin: '2024-10-24 17:30' },
  ]);
  
  const [laboratories, setLaboratories] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);

  const tabs = [
    { id: 'inventory', name: 'Inventario Actual', icon: Package },
    { id: 'purchases', name: 'Compras', icon: ShoppingCart },
    { id: 'labs', name: 'Laboratorios', icon: TestTube },
    { id: 'suppliers', name: 'Proveedores', icon: ShoppingCart },
    { id: 'cash', name: 'Control de Caja', icon: DollarSign },
    { id: 'chat', name: 'Chat Interno', icon: MessageSquare },
    { id: 'users', name: 'Staff', icon: Users },
  ];

  // Mock data for demonstration
  const inventoryItems = [
    { id: 1, name: 'Amalgama Dental', category: 'Materiales', stock: 25, minStock: 10, status: 'normal', price: 45.50 },
    { id: 2, name: 'Anestesia Local', category: 'Medicamentos', stock: 8, minStock: 15, status: 'low', price: 12.30 },
    { id: 3, name: 'Guantes Nitrilo', category: 'Equipamiento', stock: 150, minStock: 50, status: 'normal', price: 0.25 },
    { id: 4, name: 'Resina Composite', category: 'Materiales', stock: 3, minStock: 10, status: 'critical', price: 85.00 },
  ];

  const purchases = [
    { id: 1, supplier: 'DentalSupplies S.A.', date: '2024-10-20', total: 1250.00, status: 'delivered', items: 12 },
    { id: 2, supplier: 'MedEquip Ltd.', date: '2024-10-18', total: 875.50, status: 'pending', items: 8 },
    { id: 3, supplier: 'ProDental Inc.', date: '2024-10-15', total: 2100.75, status: 'delivered', items: 25 },
  ];

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'labs') {
      loadLaboratories();
    } else if (activeTab === 'suppliers') {
      loadSuppliers();
    } else if (activeTab === 'users') {
      loadStaff();
    }
  }, [activeTab]);

  const loadLaboratories = async () => {
    try {
      setLoadingLabs(true);
      const data = await laboratoryService.getLaboratories({ active: 'true' });
      setLaboratories(Array.isArray(data) ? data : data.laboratories || []);
    } catch (error) {
      console.error('Error loading laboratories:', error);
    } finally {
      setLoadingLabs(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const data = await supplierService.getSuppliers({ active: 'true' });
      setSuppliers(Array.isArray(data) ? data : data.suppliers || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const loadStaff = async () => {
    try {
      setLoadingStaff(true);
      const data = await staffService.getStaff({ active: 'true' });
      setTeamUsers(Array.isArray(data) ? data : data.staff || []);
    } catch (error) {
      console.error('Error loading staff:', error);
    } finally {
      setLoadingStaff(false);
    }
  };

  const cashMovements = [
    { id: 1, type: 'income', description: 'Pago consulta - Paciente #1234', amount: 150.00, date: '2024-10-25 14:30' },
    { id: 2, type: 'expense', description: 'Compra materiales - DentalSupplies', amount: -320.50, date: '2024-10-25 10:15' },
    { id: 3, type: 'income', description: 'Pago tratamiento - Paciente #5678', amount: 450.00, date: '2024-10-25 09:45' },
  ];

  const chatMessages = [
    { id: 1, user: 'Dr. Smith', message: 'Necesitamos más anestesia local urgente', time: '14:45', avatar: 'DS' },
    { id: 2, user: 'Recepcionista Ana', message: 'Ya hice el pedido, llega mañana', time: '14:47', avatar: 'RA' },
    { id: 3, user: 'Asistente Luis', message: 'El equipo de rayos X necesita mantenimiento', time: '15:20', avatar: 'AL' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'low': return 'text-yellow-600 bg-yellow-100';
      case 'normal': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'text-purple-600 bg-purple-100';
      case 'dentist': return 'text-blue-600 bg-blue-100';
      case 'assistant': return 'text-green-600 bg-green-100';
      case 'receptionist': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'dentist': return 'Dentista';
      case 'assistant': return 'Asistente';
      case 'receptionist': return 'Recepcionista';
      default: return role;
    }
  };

  const handleCreateUser = () => {
    setUserModalMode('create');
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleEditUser = (user: any) => {
    setUserModalMode('edit');
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleSaveUser = async (userData: any) => {
    try {
      if (userModalMode === 'create') {
        // Create new staff member
        const result = await staffService.createStaffMember(userData);
        alert('Miembro del staff creado exitosamente');
        
        // Reload staff data
        await loadStaff();
      } else {
        // Edit existing staff member - change role
        await staffService.changeRole(selectedUser._id || selectedUser.id, userData.role);
        alert('Rol del miembro del staff actualizado exitosamente');
        
        // Reload staff data
        await loadStaff();
      }
      setUserModalOpen(false);
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteUser = async (user: any) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar al miembro del staff ${user.firstName} ${user.lastName}?\n\nEsta acción desactivará al usuario.`)) {
      try {
        await staffService.deleteStaffMember(user._id || user.id);
        alert('Miembro del staff desactivado exitosamente');
        
        // Reload staff data
        await loadStaff();
      } catch (error: any) {
        alert('Error al eliminar usuario: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleViewSupplierProducts = (supplier: any) => {
    navigate(`/products?supplier=${supplier._id}&supplierName=${encodeURIComponent(supplier.name)}`);
  };

  const renderInventoryTab = () => (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Inventario Actual</h2>
          <p className="text-sm text-gray-600">Gestión y control de stock</p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center px-3 py-2 bg-dental-600 text-white rounded-md hover:bg-dental-700">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Item
          </button>
          <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Buscar productos..."
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-dental-500 focus:border-dental-500"
          />
        </div>
        <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
          <Filter className="h-4 w-4 mr-2" />
          Filtros
        </button>
      </div>

      {/* Inventory table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Producto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Categoría
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Precio
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {inventoryItems.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{item.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{item.category}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {item.stock} / {item.minStock} mín.
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                    {item.status === 'critical' ? 'Crítico' : item.status === 'low' ? 'Bajo' : 'Normal'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  €{item.price.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button className="text-dental-600 hover:text-dental-900">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="text-blue-600 hover:text-blue-900">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPurchasesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Gestión de Compras</h2>
          <p className="text-sm text-gray-600">Órdenes de compra y proveedores</p>
        </div>
        <button className="flex items-center px-3 py-2 bg-dental-600 text-white rounded-md hover:bg-dental-700">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Compra
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Proveedor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {purchases.map((purchase) => (
              <tr key={purchase.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {purchase.supplier}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {purchase.date}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {purchase.items} items
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  €{purchase.total.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    purchase.status === 'delivered' ? 'text-green-600 bg-green-100' : 'text-yellow-600 bg-yellow-100'
                  }`}>
                    {purchase.status === 'delivered' ? 'Entregado' : 'Pendiente'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button className="text-dental-600 hover:text-dental-900">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="text-blue-600 hover:text-blue-900">
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderLabsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Gestión de Laboratorios</h2>
          <p className="text-sm text-gray-600">Proveedores de servicios de laboratorio</p>
        </div>
        <button className="flex items-center px-3 py-2 bg-dental-600 text-white rounded-md hover:bg-dental-700">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Laboratorio
        </button>
      </div>

      {loadingLabs ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-dental-600"></div>
          <p className="mt-4 text-gray-600">Cargando laboratorios...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {laboratories.map((lab: any) => (
            <div key={lab._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{lab.name}</h3>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  lab.isActive ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
                }`}>
                  {lab.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Contacto:</strong> {lab.contact?.person || 'No especificado'}</p>
                <p><strong>Teléfono:</strong> {lab.contact?.phone || 'No especificado'}</p>
                <p><strong>Órdenes activas:</strong> {lab.totalOrders || 0}</p>
              </div>
              <div className="mt-4 flex gap-2">
                <button className="flex-1 px-3 py-2 text-sm text-dental-600 border border-dental-300 rounded-md hover:bg-dental-50">
                  Ver Órdenes
                </button>
                <button className="px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50">
                  <Edit className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {laboratories.length === 0 && !loadingLabs && (
            <div className="col-span-full text-center py-12">
              <TestTube className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay laboratorios registrados</h3>
              <p className="text-gray-600">Comienza agregando tu primer laboratorio</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderSuppliersTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Gestión de Proveedores</h2>
          <p className="text-sm text-gray-600">Proveedores de materiales y equipos</p>
        </div>
        <button className="flex items-center px-3 py-2 bg-dental-600 text-white rounded-md hover:bg-dental-700">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Proveedor
        </button>
      </div>

      {loadingSuppliers ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-dental-600"></div>
          <p className="mt-4 text-gray-600">Cargando proveedores...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {suppliers.map((supplier: any) => (
            <div key={supplier._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{supplier.name}</h3>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  supplier.isActive ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
                }`}>
                  {supplier.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Contacto:</strong> {supplier.contact?.person || 'No especificado'}</p>
                <p><strong>Teléfono:</strong> {supplier.contact?.phone || 'No especificado'}</p>
                <p><strong>Categorías:</strong> {supplier.categories?.slice(0, 2).join(', ') || 'Ninguna'}</p>
              </div>
              <div className="mt-4 flex gap-2">
                <button 
                  onClick={() => handleViewSupplierProducts(supplier)}
                  className="flex-1 px-3 py-2 text-sm text-dental-600 border border-dental-300 rounded-md hover:bg-dental-50"
                >
                  Ver Productos
                </button>
                <button className="px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50">
                  <Edit className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {suppliers.length === 0 && !loadingSuppliers && (
            <div className="col-span-full text-center py-12">
              <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay proveedores registrados</h3>
              <p className="text-gray-600">Comienza agregando tu primer proveedor</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCashTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Control de Caja</h2>
          <p className="text-sm text-gray-600">Ingresos, gastos y movimientos financieros</p>
        </div>
        <button className="flex items-center px-3 py-2 bg-dental-600 text-white rounded-md hover:bg-dental-700">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Movimiento
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ingresos del Día</p>
              <p className="text-2xl font-semibold text-gray-900">€1,250.00</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingDown className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Gastos del Día</p>
              <p className="text-2xl font-semibold text-gray-900">€320.50</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DollarSign className="h-8 w-8 text-dental-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Balance del Día</p>
              <p className="text-2xl font-semibold text-gray-900">€929.50</p>
            </div>
          </div>
        </div>
      </div>

      {/* Movements table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Movimientos Recientes</h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Monto
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha/Hora
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cashMovements.map((movement) => (
              <tr key={movement.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    movement.type === 'income' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                  }`}>
                    {movement.type === 'income' ? 'Ingreso' : 'Gasto'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {movement.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <span className={movement.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                    €{Math.abs(movement.amount).toFixed(2)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {movement.date}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderChatTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Chat Interno</h2>
          <p className="text-sm text-gray-600">Notificaciones y comunicación del equipo</p>
        </div>
      </div>

      {/* Chat interno para notificaciones y mensajes */}
      <div className="h-96">
        <InternalChat className="h-full" />
      </div>
    </div>
  );

  const renderUsersTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Staff</h2>
          <p className="text-sm text-gray-600">Administración del personal de la clínica</p>
        </div>
        <button 
          onClick={handleCreateUser}
          className="flex items-center px-3 py-2 bg-dental-600 text-white rounded-md hover:bg-dental-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Usuario
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuario
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Último Login
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {teamUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      <div className="h-8 w-8 bg-dental-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user.status === 'active' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                  }`}>
                    {user.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.lastLogin}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleEditUser(user)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(user)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'inventory': return renderInventoryTab();
      case 'purchases': return renderPurchasesTab();
      case 'labs': return renderLabsTab();
      case 'suppliers': return renderSuppliersTab();
      case 'cash': return renderCashTab();
      case 'chat': return renderChatTab();
      case 'users': return renderUsersTab();
      default: return renderInventoryTab();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Administración Interna</h1>
        <p className="text-gray-600">Gestión integral de la clínica dental</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-dental-500 text-dental-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {renderActiveTab()}

      {/* User Management Modal */}
      <UserManagementModal
        isOpen={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSave={handleSaveUser}
        mode={userModalMode}
        user={selectedUser}
      />
    </div>
  );
};

export default Administration;